import './index.scss';
import '@pulsarml/avalon-ui/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import * as serviceWorker from './serviceWorker';
import ApiClient from './api/index';
import Favicon from './components/favicon';
import { Provider } from 'react-redux';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ReduxToastr from './utils/redux-toastr';
import api from './api/methods';
import store from './store';
import Amplify from 'aws-amplify';
import * as Sentry from '@sentry/react';
import awsconfig from './aws-exports';
import { ToastContainer } from 'react-toastify';
import { I18n, syncTranslation } from '@utils/i18n';
import { registerAllServices } from '_clean-code/services/context/register';
import ReactGA from 'react-ga4';
import { initiateMirage } from '@utils/networkMocking';
import { ToastProvider } from '@pulsarml/ui.react.components';
import App from './app';
import { sessionService } from 'redux-react-session';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { clarity } from 'react-microsoft-clarity';
import 'moment/locale/es';
import 'moment/locale/en-gb';
import { Toast } from '@pulsarml/avalon-ui';
import { Logger } from '_clean-code/utils/logger';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  PUBLIC_POSTHOG_KEY,
  PUBLIC_POSTHOG_HOST
} from './constants/posthog.js';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { appVersion } from './constants/config';
import {
  shouldLogError,
  shouldThrottleError
} from '_clean-code/utils/errorHandling';

if (import.meta.env.PROD) {
  posthog.init(PUBLIC_POSTHOG_KEY, {
    api_host: PUBLIC_POSTHOG_HOST,
    autocapture: {
      dom_event_allowlist: ['click']
    }
  });
}

const queryClient = new QueryClient();

sessionService
  .loadUser()
  .then((user) => {
    Logger.info('User loaded from session: ', user);
    ReactGA.initialize([
      {
        trackingId: import.meta.env.VITE_APP_G_TAG,
        gtagOptions: {
          user_id: user.cognitoUsername
        },
        gaOptions: {
          userId: user.cognitoUsername
        }
      }
    ]);
  })
  .catch(() => {
    Logger.info('No user found in session');
    ReactGA.initialize([
      {
        trackingId: import.meta.env.VITE_APP_G_TAG
        //gtagOptions: { user_id: 'anonymous' }
      }
    ]);
  });

/** Configure amplify */
Amplify.configure({
  ...awsconfig,
  oauth: {
    ...awsconfig.oauth,
    redirectSignIn: window.location.origin + awsconfig.oauth.redirectSignIn,
    redirectSignOut: window.location.origin + awsconfig.oauth.redirectSignOut
  }
});

clarity.init(import.meta.env.VITE_APP_CLARITY_ID);

if (import.meta.env.PROD) {
  // Sentry error tracking
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error']
      })
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 10% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: `web-frontend@${appVersion}`,
    beforeSend: (event, hint) => {
      const error = hint?.originalException;
      if (
        error instanceof Error &&
        (!shouldLogError(error) || shouldThrottleError(error))
      ) {
        return null;
      }
      return event;
    }
  });
}

// Test with local mock data
if (import.meta.env.VITE_APP_WITH_MOCK_DATA === 'true') {
  initiateMirage();
}

registerAllServices();

const render = () => {
  // SMOKY BLACK
  // #100B00
  // ST. PATRICK'S BLUE
  // #274C77
  // GLAUCOUS
  // #6279B8
  // BABY BLUE EYES
  // #A3CEF1
  // SHINY SHAMROCK
  // #4C9F70
  const fallbackColor = '#4bd1a0';

  const setCssVariable = (name: string, value: string) => {
    if (document.documentElement) {
      document.documentElement.style.setProperty(name, value);
    }
  };

  setCssVariable('--primary', fallbackColor);
  setCssVariable('--primary-dark', fallbackColor);
  setCssVariable('--primary-light', fallbackColor);

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={I18n._locale === 'us' ? 'en-gb' : 'es'}
    >
      <PostHogProvider client={posthog}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <App />
            </ToastProvider>
            <ToastContainer style={{ zIndex: 999999 }} />
            <ReduxToastr />
            <Toast duration={3000} />
            <Favicon />
          </QueryClientProvider>
        </Provider>
      </PostHogProvider>
    </LocalizationProvider>
  );
};

serviceWorker.unregister();

ApiClient.injectApiMethods(api);

syncTranslation(store.getState().i18n.locale);

render();
